import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  useGalleryFolders,
  useGalleryFiles,
  usePublicGalleryFiles,
  useSpatulaCatalogFiles,
  galleryQueryKeys,
} from './useGallery'
import { useDebounce } from '../../../hooks/useDebounce'
import { useGalleryContext } from '../context/GalleryContext'
import type {
  PantryMediaFile,
  PantryMediaFileEdits,
  SourceFilter,
} from '../components/GalleryPicker/types'
import { galleryApis } from '../../../gateways/breadstack/galleryApis'
import type { GalleryFolder, GalleryFile } from '../../../gateways/breadstack/galleryApis'

const MEDIA_FOLDER_STATIC = {
  ALL_FILE: 'all',
  UNSORTED: 'unsorted',
}

// Uncopied Breadstack catalog images (product picker): at most this many copies
// per opening of the picker, each given this long to finish.
export const MAX_SPATULA_COPIES = 5
const SPATULA_COPY_TIMEOUT_MS = 15_000
const SPATULA_POLL_INTERVAL_MS = 500

export type SpatulaCopyState = 'copying' | 'failed'

type PageSource = 'tenant' | 'public' | 'spatula'

// A list that matched nothing counts as 0 pages. The API reports total_pages = 1
// for an empty result, which used to leave the owner on an empty "No images found"
// page with the next list's results hidden behind "Load more".
const pagesOf = (data?: { total_pages: number; stats?: { total_items: number } }) =>
  data && (data.stats?.total_items ?? 0) > 0 ? data.total_pages : 0

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Each list fills its own pages, so a short list (e.g. 1 copied Breadstack image)
// would leave the next list's results behind "Load more". Keep loading pages
// automatically until the grid holds at least one picker page of results.
const AUTO_FILL_MIN_FILES = 28

interface UseGalleryStateProps {
  orgId: string
  isFileImage: (file: unknown) => boolean
  getFolderId: (folderId: string) => string
  // When false, all gallery data fetching is suspended. Defaults to true so
  // existing/external callers keep their current always-on behavior. The
  // GalleryPicker threads its `open` prop here so a mounted-but-closed picker
  // (e.g. inside an always-rendered SlideDrawer) fires no requests until opened.
  enabled?: boolean
  // Product editor only: also list Breadstack catalog images nobody has copied
  // yet, after the Breadstack results, when there is search text.
  includeSpatulaCatalog?: boolean
}

export const useGalleryState = ({
  orgId,
  getFolderId,
  enabled = true,
  includeSpatulaCatalog = false,
}: UseGalleryStateProps) => {
  const { storeId: storeIdFromContext } = useGalleryContext()
  const { storeId: storeIdFromParams } = useParams<{ storeId: string }>()
  // URL params are the reliable source; context storeId is an optional override
  const storeId = storeIdFromContext ?? storeIdFromParams

  const [selectedFolderId, setSelectedFolderId] = useState(MEDIA_FOLDER_STATIC.ALL_FILE)
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [contentType, setContentType] = useState('')
  const [assetType, setAssetType] = useState('')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [fileSelected, setFileSelected] = useState<PantryMediaFile[]>([])
  // The picked image whose details the picker shows and edits
  const [activeFile, setActiveFile] = useState<PantryMediaFile | null>(null)
  // Load-more accumulation
  const [accumulatedFiles, setAccumulatedFiles] = useState<PantryMediaFile[]>([])
  const [loadedUpToPage, setLoadedUpToPage] = useState(0)
  // Copy-on-pick of uncopied catalog images, keyed by the item's _id
  const [spatulaCopies, setSpatulaCopies] = useState<Record<string, SpatulaCopyState>>({})
  const [spatulaCopiesStarted, setSpatulaCopiesStarted] = useState(0)
  const [spatulaLimitReached, setSpatulaLimitReached] = useState(false)
  const copyRunRef = useRef(0)

  const debouncedSearch = useDebounce(search, 300)
  const queryClient = useQueryClient()

  const foldersQuery = useGalleryFolders(orgId, enabled)

  // Source gates which streams are active. Mirrors the pattern in MediaPage.
  // `enabled` (picker open) is ANDed on top so nothing fetches while closed.
  const tenantEnabled = enabled && sourceFilter !== 'breadstack'
  const publicEnabled = enabled && sourceFilter !== 'tenant' && Boolean(storeId && orgId)
  // Catalog images are product images once copied, so only under All Types /
  // Product Image, and only when the owner has typed something.
  const spatulaEnabled =
    includeSpatulaCatalog &&
    publicEnabled &&
    debouncedSearch.trim() !== '' &&
    contentType !== 'video' &&
    (assetType === '' || assetType === 'product_image')

  // ── Page-1 meta queries fired simultaneously on mount ──────────────────────
  // Both run in parallel so total_pages and stats for both sources are known
  // immediately. React Query deduplicates each with the real data query when
  // currentPage === 1 / publicApiPage === 1, so no extra requests are made.

  const currentApiMetaQuery = useGalleryFiles({
    orgId,
    folderId: selectedFolderId,
    page: 1,
    search: debouncedSearch,
    contentType,
    assetType,
    enabled: tenantEnabled,
  })

  const publicApiMetaQuery = usePublicGalleryFiles({
    storeId,
    orgId,
    page: 1,
    search: debouncedSearch,
    contentType,
    assetType,
    enabled: publicEnabled,
  })

  const spatulaMetaQuery = useSpatulaCatalogFiles({
    storeId,
    orgId,
    page: 1,
    search: debouncedSearch,
    contentType,
    assetType,
    enabled: spatulaEnabled,
  })

  const currentApiTotalPages = tenantEnabled ? pagesOf(currentApiMetaQuery.data) : 0
  const publicApiTotalPages = publicEnabled ? pagesOf(publicApiMetaQuery.data) : 0
  const spatulaTotalPages = spatulaEnabled ? pagesOf(spatulaMetaQuery.data) : 0
  const totalPages = currentApiTotalPages + publicApiTotalPages + spatulaTotalPages

  // Combined stats from both sources (available as soon as both meta queries resolve)
  const combinedStats = useMemo(() => {
    const a = currentApiMetaQuery.data?.stats
    const b = publicApiMetaQuery.data?.stats
    return {
      total_items: (a?.total_items ?? 0) + (b?.total_items ?? 0),
      banner: (a?.banner ?? 0) + (b?.banner ?? 0),
      brand_logo: (a?.brand_logo ?? 0) + (b?.brand_logo ?? 0),
      category: (a?.category ?? 0) + (b?.category ?? 0),
      product_image: (a?.product_image ?? 0) + (b?.product_image ?? 0),
    }
  }, [currentApiMetaQuery.data?.stats, publicApiMetaQuery.data?.stats])

  // ── Per-page data queries ──────────────────────────────────────────────────
  // The grid walks the active lists in order — My Uploads, then Breadstack, then
  // uncopied catalog images — and page N belongs to whichever list it falls in.
  // A list's page count is only trusted once its page-1 meta query has settled,
  // so we wait for each earlier list before routing a page past it.
  const tenantMetaSettled = !tenantEnabled || !currentApiMetaQuery.isLoading
  const publicMetaSettled = !publicEnabled || !publicApiMetaQuery.isLoading
  const spatulaMetaSettled = !spatulaEnabled || !spatulaMetaQuery.isLoading

  let activeSource: PageSource | null = null
  let sourcePage = 1
  if (tenantMetaSettled) {
    if (currentPage <= currentApiTotalPages) {
      activeSource = 'tenant'
      sourcePage = currentPage
    } else if (publicMetaSettled) {
      if (currentPage <= currentApiTotalPages + publicApiTotalPages) {
        activeSource = 'public'
        sourcePage = currentPage - currentApiTotalPages
      } else if (spatulaMetaSettled && currentPage <= totalPages) {
        activeSource = 'spatula'
        sourcePage = currentPage - currentApiTotalPages - publicApiTotalPages
      }
    }
  }
  const metaSettled = tenantMetaSettled && publicMetaSettled && spatulaMetaSettled

  // Current API — serves pages 1..currentApiTotalPages
  const filesQuery = useGalleryFiles({
    orgId,
    folderId: selectedFolderId,
    page: activeSource === 'tenant' ? sourcePage : 1,
    search: debouncedSearch,
    contentType,
    assetType,
    enabled: tenantEnabled && activeSource === 'tenant',
  })

  // Public API — serves the pages after My Uploads (reuses meta cache on page 1)
  const publicFilesQuery = usePublicGalleryFiles({
    storeId,
    orgId,
    page: activeSource === 'public' ? sourcePage : 1,
    search: debouncedSearch,
    contentType,
    assetType,
    enabled: publicEnabled && activeSource === 'public',
  })

  // Uncopied catalog images — serve the pages after Breadstack
  const spatulaFilesQuery = useSpatulaCatalogFiles({
    storeId,
    orgId,
    page: activeSource === 'spatula' ? sourcePage : 1,
    search: debouncedSearch,
    contentType,
    assetType,
    enabled: spatulaEnabled && activeSource === 'spatula',
  })
  const activeQuery =
    activeSource === 'tenant'
      ? filesQuery
      : activeSource === 'public'
        ? publicFilesQuery
        : activeSource === 'spatula'
          ? spatulaFilesQuery
          : null

  // Reset accumulation when folder/search/filters/source change.
  // Per project memory: source filter must also `removeQueries` for both streams,
  // not just rely on `invalidateQueries`, so stale-while-revalidate doesn't replay
  // the append effect with old data.
  // Skip the initial mount run: with two pickers mounting (thumbnail + banner),
  // each instance's mount-time removeQueries was evicting the other's in-flight
  // queries, defeating React Query's dedup and forcing refetches. We only reset on
  // an actual folder/search/filter/source change, not on first mount.
  const didMountResetRef = useRef(false)
  useEffect(() => {
    if (!didMountResetRef.current) {
      didMountResetRef.current = true
      return
    }
    queryClient.removeQueries({ queryKey: [...galleryQueryKeys.all(), 'files'] })
    queryClient.removeQueries({ queryKey: [...galleryQueryKeys.all(), 'public-files'] })
    queryClient.removeQueries({ queryKey: [...galleryQueryKeys.all(), 'spatula-files'] })
    setAccumulatedFiles([])
    setLoadedUpToPage(0)
    setCurrentPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolderId, debouncedSearch, contentType, assetType, sourceFilter])

  // Append each newly loaded page into the accumulated list.
  // Guard: don't mark loaded if data is still undefined — the query may not have been
  // enabled yet (e.g. public API before isPublicPage becomes true).
  useEffect(() => {
    if (currentPage <= loadedUpToPage || !activeQuery) return
    if (activeQuery.isLoading || activeQuery.data === undefined) return

    // Catalog images are Breadstack's too, so they carry the same badge.
    const newFiles: PantryMediaFile[] =
      activeSource === 'tenant'
        ? ((activeQuery.data.files ?? []) as PantryMediaFile[])
        : (activeQuery.data.files ?? []).map((f) => ({ ...f, _badge: 'breadstack' as string }))

    setAccumulatedFiles((prev) => [...prev, ...newFiles])
    setLoadedUpToPage(currentPage)
  }, [currentPage, activeSource, activeQuery, activeQuery?.data, activeQuery?.isLoading, loadedUpToPage])

  // Each opening of the picker starts a fresh copy allowance; closing it stops
  // any readiness polling still in flight (nothing keeps polling a closed picker).
  useEffect(() => {
    copyRunRef.current += 1
    setSpatulaCopies({})
    setSpatulaCopiesStarted(0)
    setSpatulaLimitReached(false)
  }, [enabled])
  useEffect(
    () => () => {
      copyRunRef.current += 1
    },
    [],
  )

  const allFileCount = useMemo(() => {
    if (!foldersQuery.data?.folders) return 0
    const folderTotal = foldersQuery.data.folders.length
      ? foldersQuery.data.folders
          .map((f: GalleryFolder) => f.count)
          .reduce((a: number, c: number) => a + c, 0)
      : 0
    return folderTotal + (foldersQuery.data?.unsortedFileCount || 0)
  }, [foldersQuery.data])

  const staticFolders: GalleryFolder[] = useMemo(
    () => [
      { id: MEDIA_FOLDER_STATIC.ALL_FILE, name: 'All Files', count: allFileCount },
      { id: MEDIA_FOLDER_STATIC.UNSORTED, name: 'Unsorted', count: foldersQuery.data?.unsortedFileCount || 0 },
    ],
    [allFileCount, foldersQuery.data?.unsortedFileCount],
  )

  const filesMatch = (f1: PantryMediaFile, f2: PantryMediaFile) => {
    if (f1._id && f2._id === f1._id) return true
    const f1Src = f1.src || f1.public_url
    const f2Src = f2.src || f2.public_url
    if (f1Src && f2Src && f1Src === f2Src) return true
    const f1Id = f1.id_select || f1._id || f1.id
    const f2Id = f2.id_select || f2._id || f2.id
    if (f1Id && f2Id && f1Id === f2Id) return true
    return false
  }

  const normaliseSelected = (file: PantryMediaFile): PantryMediaFile => ({
    ...file,
    _id: file._id,
    id: file._id || file.id,
    id_select: file._id || file.id,
    src: file.src || file.public_url,
    thumbnail: file.public_url || file.src,
  })

  // Copy a picked catalog image into Breadstack, then swap it for the copy —
  // in the grid and in the selection — so saving uses the Breadstack URL.
  const copySpatulaImage = useCallback(
    async (file: PantryMediaFile) => {
      const itemId = file._id as string
      const run = copyRunRef.current
      const params = {
        store_id: storeId!,
        organization_id: orgId,
        spatula_doc_id: file.spatula_doc_id!,
        image_url: file.spatula_image_url!,
      }
      let copied: GalleryFile | undefined
      try {
        let res = await galleryApis.copySpatulaImage(params)
        const deadline = Date.now() + SPATULA_COPY_TIMEOUT_MS
        while (res.status !== 'ready' && Date.now() < deadline) {
          await sleep(SPATULA_POLL_INTERVAL_MS)
          if (copyRunRef.current !== run) return
          res = await galleryApis.getSpatulaImage(params)
        }
        if (res.status === 'ready') copied = res.file
      } catch {
        copied = undefined
      }
      if (copyRunRef.current !== run) return

      if (copied) {
        const asset: PantryMediaFile = { ...(copied as PantryMediaFile), _badge: 'breadstack' }
        const picked = normaliseSelected(asset)
        setAccumulatedFiles((prev) => prev.map((f) => (f._id === itemId ? asset : f)))
        // Keep what the owner typed while the copy ran.
        setFileSelected((prev) =>
          prev.map((f) => (f._id === itemId ? { ...picked, _edits: f._edits } : f)),
        )
        setActiveFile((prev) => (prev?._id === itemId ? picked : prev))
        setSpatulaCopies(({ [itemId]: _done, ...rest }) => rest)
      } else {
        setFileSelected((prev) => prev.filter((f) => f._id !== itemId))
        setSpatulaCopies((prev) => ({ ...prev, [itemId]: 'failed' }))
      }
    },
    [storeId, orgId],
  )

  const handleUnselectFile = useCallback(
    (file: PantryMediaFile) => {
      // Unpicking a catalog image mid-copy lets the copy finish; it just isn't added.
      setSpatulaLimitReached(false)
      setFileSelected(fileSelected.filter((f) => !filesMatch(file, f)))
    },
    [fileSelected],
  )

  const handleToggleFile = useCallback(
    (file: PantryMediaFile, multiSelect: boolean = false) => {
      const isSelected = fileSelected.some((f) => filesMatch(file, f))

      if (isSelected) {
        // With several picked, a click shows that image's details; the tick unpicks it.
        if (multiSelect) {
          setSpatulaLimitReached(false)
          setActiveFile(file)
          return
        }
        handleUnselectFile(file)
        return
      }

      if (file.spatula_doc_id && file._id) {
        const state = spatulaCopies[file._id]
        if (state !== 'copying') {
          if (state === 'failed') return
          if (spatulaCopiesStarted >= MAX_SPATULA_COPIES) {
            setSpatulaLimitReached(true)
            return
          }
          setSpatulaCopiesStarted((n) => n + 1)
          setSpatulaCopies((prev) => ({ ...prev, [file._id as string]: 'copying' }))
          // Never rejects: failures are handled inside and shown on the tile.
          copySpatulaImage(file)
        }
      }

      // The limit notice only explains the click that was just refused.
      setSpatulaLimitReached(false)
      const normalised = normaliseSelected(file)
      if (multiSelect) {
        setFileSelected([...fileSelected, normalised])
      } else {
        setFileSelected([normalised])
      }
      setActiveFile(normalised)
    },
    [fileSelected, spatulaCopies, spatulaCopiesStarted, copySpatulaImage, handleUnselectFile],
  )

  const isCopyingSpatula = fileSelected.some((f) => f._id && spatulaCopies[f._id] === 'copying')

  const isFileSelected = (file: PantryMediaFile) =>
    fileSelected.some((f) => filesMatch(file, f))

  // Falls back to the first picked image, e.g. when the active one was unpicked.
  const activeSelected =
    (activeFile && fileSelected.find((f) => filesMatch(activeFile, f))) ||
    fileSelected.find((f) => f && Object.keys(f).length > 0)

  const isFileActive = (file: PantryMediaFile) =>
    Boolean(activeSelected && filesMatch(file, activeSelected))

  // Record what the owner typed for the active picked image.
  const editActiveFile = (edits: PantryMediaFileEdits) => {
    if (!activeSelected) return
    setFileSelected((prev) =>
      prev.map((f) =>
        filesMatch(f, activeSelected) ? { ...f, _edits: { ...f._edits, ...edits } } : f,
      ),
    )
  }

  // Until every list's page count is known the page can't be routed, so that
  // counts as loading too.
  const isLoadingFiles = currentPage === 1 ? (activeQuery ? activeQuery.isLoading : !metaSettled) : false
  const isLoadingMore =
    currentPage > loadedUpToPage && (activeQuery ? activeQuery.isLoading : !metaSettled)
  const isLoadingStats = currentApiMetaQuery.isLoading || publicApiMetaQuery.isLoading

  const hasNextPage = totalPages > 0 && currentPage < totalPages

  // Top up a short grid from the following pages/lists without a click. Only
  // advances once the current page has been appended, so it cannot run ahead.
  useEffect(() => {
    if (!enabled || loadedUpToPage < currentPage) return
    if (accumulatedFiles.length >= AUTO_FILL_MIN_FILES || !hasNextPage) return
    setCurrentPage((prev) => prev + 1)
  }, [enabled, loadedUpToPage, currentPage, accumulatedFiles.length, hasNextPage])

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isLoadingMore) {
      setCurrentPage((prev) => prev + 1)
    }
  }, [hasNextPage, isLoadingMore])

  return {
    // Query state
    folders: foldersQuery.data?.folders || [],
    staticFolders,
    files: accumulatedFiles,
    filteredFiles: accumulatedFiles,
    isLoadingFolders: foldersQuery.isLoading,
    isLoadingFiles,
    isLoadingMore,
    isLoadingStats,
    totalPages,
    combinedStats,
    currentPage,
    hasNextPage,

    // Local UI state
    selectedFolderId,
    search,
    contentType,
    assetType,
    sourceFilter,
    fileSelected,

    // Actions
    setSelectedFolderId,
    setSearch,
    setContentType,
    setAssetType,
    setSourceFilter,
    setFileSelected,
    handleToggleFile,
    handleUnselectFile,
    isFileSelected,
    handleLoadMore,

    // The picked image whose details are shown and edited
    activeFile: activeSelected,
    setActiveFile,
    isFileActive,
    editActiveFile,

    // Copy-on-pick of uncopied catalog images (product picker)
    spatulaCopies,
    isCopyingSpatula,
    spatulaLimitReached,

    // Constants
    MEDIA_FOLDER_STATIC,
  }
}
